/**
 * The assistant's transport.
 *
 * Kept apart from `api.ts` because this is the one endpoint that streams.
 * The rest of the site does request/response JSON; this reads a body as it
 * arrives and has to survive being cut off halfway.
 *
 * **Not `EventSource`.** The browser's SSE client only issues GET requests
 * and cannot send a body, and this endpoint is a POST carrying the message.
 * So the stream is read off `fetch` and the SSE framing is parsed by hand —
 * which is a handful of lines and also means an HTTP error arrives as a
 * status code we can read rather than as an opaque `onerror`.
 */

import { API_BASE } from "./apiBase";

const BASE = `${API_BASE}/api/v1/chat`;

export interface ChatSession {
  session_id: string;
  chat_enabled: boolean;
  reason: string | null;
  suggestions: string[];
}

/** Everything the server reports about how an answer was produced. */
export interface AnswerReceipts {
  wasRefused: boolean;
  refusalReason: string | null;
  modelUsed: string | null;
  /** Absent on refusals — they never reach the model, so there is no
   *  latency to report. */
  latencyMs: number | null | undefined;
  degraded: boolean;
  sources: string[];
}

export interface StreamHandlers {
  onToken: (text: string) => void;
  onSources: (slugs: string[]) => void;
  onDone: (receipts: AnswerReceipts) => void;
  onError: (message: string) => void;
}

export async function startSession(): Promise<ChatSession> {
  const response = await fetch(`${BASE}/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  if (!response.ok) throw new Error(`session ${String(response.status)}`);
  return (await response.json()) as ChatSession;
}

interface ServerError {
  error?: { message?: string };
}

/**
 * One SSE frame: `event: name\ndata: {...}\n\n`.
 *
 * Frames are split on the blank line, and a partial frame is carried over to
 * the next chunk — a network read boundary lands mid-frame often enough that
 * parsing each chunk independently produces silent JSON errors under exactly
 * the conditions (slow connections) where it matters most.
 */
function parseFrame(frame: string): { name: string; data: unknown } | null {
  let name = "message";
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) name = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;

  try {
    return { name, data: JSON.parse(dataLines.join("\n")) };
  } catch {
    return null;
  }
}

export async function streamMessage(
  sessionId: string,
  message: string,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId, message }),
      signal,
    });
  } catch {
    handlers.onError(
      "Couldn't reach the server. It may be waking up — try again in a few seconds.",
    );
    return;
  }

  if (!response.ok || !response.body) {
    // The server refuses in words, with a status. Surfacing its message
    // rather than a generic one matters here: a rate-limit and a spent budget
    // are different situations and the backend already phrases both.
    let detail = "The assistant is unavailable right now.";
    try {
      const body = (await response.json()) as ServerError;
      if (body.error?.message) detail = body.error.message;
    } catch {
      /* not JSON — keep the default */
    }
    handlers.onError(detail);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const sources: string[] = [];
  let settled = false;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let split = buffer.indexOf("\n\n");
      while (split !== -1) {
        const frame = parseFrame(buffer.slice(0, split));
        buffer = buffer.slice(split + 2);
        split = buffer.indexOf("\n\n");
        if (!frame) continue;

        if (frame.name === "token") {
          handlers.onToken((frame.data as { text: string }).text);
        } else if (frame.name === "sources") {
          const slugs = (frame.data as { slugs: string[] }).slugs;
          sources.push(...slugs);
          handlers.onSources(slugs);
        } else if (frame.name === "done") {
          const done_ = frame.data as {
            was_refused: boolean;
            refusal_reason: string | null;
            model_used: string | null;
            latency_ms: number | null;
            degraded: boolean;
          };
          settled = true;
          handlers.onDone({
            wasRefused: done_.was_refused,
            refusalReason: done_.refusal_reason,
            modelUsed: done_.model_used,
            latencyMs: done_.latency_ms,
            degraded: done_.degraded,
            sources,
          });
        }
      }
    }
  } catch {
    if (!settled) handlers.onError("The connection dropped mid-answer.");
    return;
  }

  // A stream that ends without a `done` frame is a truncated answer, not a
  // finished one. Saying so is the whole point of a site about failure paths.
  if (!settled) handlers.onError("The answer was cut off before it finished.");
}
